import { createRoot } from 'react-dom/client'
import { Toaster as OldToaster } from '@/components/ui/toaster'
import App from './App'
import './index.css'
import { trackWebVitals } from '@/utils/webVitals'

// =============================================================================
// Global Error Handlers - Catch errors that escape React's error boundaries
// =============================================================================

const RELOAD_KEY = 'app_reload_attempted';

// Show fallback UI when app completely fails
function showFallbackUI(message: string) {
  const rootElement = document.getElementById("root");
  if (rootElement) {
    rootElement.innerHTML = `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; font-family: Inter, system-ui, sans-serif; background: #0a0a0a;">
        <div style="text-align: center; padding: 2rem; max-width: 400px;">
          <div style="width: 48px; height: 48px; margin: 0 auto 1rem; border-radius: 50%; background: rgba(220, 38, 38, 0.1); display: flex; align-items: center; justify-content: center;">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <h1 style="color: #f5f5f5; margin-bottom: 0.5rem; font-size: 1.25rem; font-weight: 600;">Something went wrong</h1>
          <p style="color: #a3a3a3; margin-bottom: 1.5rem; font-size: 0.875rem;">${message}</p>
          <button onclick="sessionStorage.removeItem('${RELOAD_KEY}'); window.location.reload()" style="background: #2563eb; color: white; padding: 0.625rem 1.25rem; border: none; border-radius: 0.375rem; cursor: pointer; font-size: 0.875rem; font-weight: 500;">
            Refresh Page
          </button>
        </div>
      </div>
    `;
  }
}

// Handle chunk/module load failures (common after deployments with cached HTML)
function handleChunkLoadError(error: Error | string): boolean {
  const errorMessage = typeof error === 'string' ? error : error?.message || '';
  const isChunkError = 
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('Loading CSS chunk') ||
    errorMessage.includes('error loading dynamically imported module');
  
  if (isChunkError) {
    // Only attempt one automatic reload per session to prevent infinite loops
    const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
    if (!alreadyReloaded) {
      console.warn('[App Recovery] Chunk load failed, attempting reload...');
      sessionStorage.setItem(RELOAD_KEY, 'true');
      // Force a hard reload bypassing cache
      window.location.href = window.location.href.split('?')[0] + '?_reload=' + Date.now();
      return true;
    } else {
      console.error('[App Recovery] Chunk load failed after reload attempt');
      showFallbackUI('Failed to load application resources. Please try clearing your browser cache.');
      return true;
    }
  }
  return false;
}

// Global error handler for uncaught errors
window.onerror = (message, source, lineno, colno, error) => {
  console.error('[Global Error]', { message, source, lineno, colno, error });
  
  if (error && handleChunkLoadError(error)) {
    return true; // Prevent default error handling
  }
  
  // Let React's error boundary handle most errors
  return false;
};

// Global handler for unhandled promise rejections
window.onunhandledrejection = (event) => {
  console.error('[Unhandled Rejection]', event.reason);
  
  if (event.reason && handleChunkLoadError(event.reason)) {
    event.preventDefault();
    return;
  }
};

// Clear reload flag on successful app load (set after React mounts)
function clearReloadFlag() {
  // Wait a bit to ensure the app actually loaded successfully
  setTimeout(() => {
    sessionStorage.removeItem(RELOAD_KEY);
  }, 3000);
}

// =============================================================================
// React App Initialization
// =============================================================================

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

const root = createRoot(rootElement);

try {
  root.render(
    <>
      <App />
      <OldToaster />
    </>
  );

  // Track Web Vitals and clear reload flag after React is mounted
  trackWebVitals();
  clearReloadFlag();
} catch (error) {
  console.error("[App Init] Failed to render app:", error);
  showFallbackUI('The application failed to start. Please refresh the page.');
}
